import React, { useEffect, useRef, useState, Suspense, lazy } from "react";
import Container from "react-bootstrap/Container";
import { EVENT_TEMPLATES_CDN_URL } from "../../Utils";
import SpinnerLoader from "../../Utils/SpinnerLoader";
import MediaQuery from "react-responsive";
import "./index.css";

const AttendeeList = lazy(() => import("../AttendeeList"));
const CongressSection = lazy(() => import("../CongressSection"));
const SponsorSection = lazy(() => import("../SponsorSection"));
const Agendasection = lazy(() => import("../Agendasection"));
const PastEventsCarousel = lazy(() => import("../PastEvents"));
const BuyTicket = lazy(() => import("../BuyTicket"));
const Speakers = lazy(() => import("../Speakers/Speakers"));
const RamaSwamySection = lazy(() => import("../RamaSwamySection/RamaSwamySection"));
const Footer = lazy(() => import("../../FooterNew/footerChanges"));

export default function EventBody({ themeId, eventType, eventCreatedUnderDomain, eventPageData, preEventData, isMobile, webpageData, carouselRef }) {
    const [bodyBgImgSrc, setBodyBgImgSrc] = useState("");
    const [imageHeight, setImageHeight] = useState("auto");
    const [showOtherComponents, setShowOtherComponents] = useState(false);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    
    // Check if current time is after July 8, 2025 5PM ET
    const isAfterRegistrationDeadline = () => {
        const now = new Date();
        const deadline = new Date('2025-07-08T17:00:00-04:00'); // July 8, 2025 5PM ET
        // const deadline = new Date('2025-07-08T00:42:00-07:00');
        return now >= deadline;
    };
    
    const showBuyTicket = !isAfterRegistrationDeadline();

    // Create a ref for the container of BuyTicket
    const buyTicketContainerRef = useRef(null);
    const absoluteContentRef = useRef(null);
    const mainImageRef = useRef(null);

    // Global function to force load components and scroll
    useEffect(() => {
        // Flag to track if we've done the scrolling operation
        let hasScrolledOnInitialLoad = false;

        // Define global function
        window.scrollToTickets = () => {
            // First make sure the component is visible
            setShowOtherComponents(true);

            // After state update, try multiple times to scroll
            const scrollAttempt = (attempts = 0) => {
                if (attempts >= 10) return; // Give up after 10 attempts (increased from 5)

                setTimeout(() => {
                    // Try to get the container element using the ref
                    const containerEl = buyTicketContainerRef.current;
                    if (containerEl) {
                        // Get the element's position
                        const rect = containerEl.getBoundingClientRect();
                        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

                        // Calculate position with offset (-50px)
                        const offsetPosition = scrollTop + rect.top - 50;

                        // Scroll to the adjusted position
                        window.scrollTo({
                            top: offsetPosition,
                            behavior: 'smooth'
                        });


                    } else {

                        scrollAttempt(attempts + 1);
                    }
                }, 500 + (attempts * 100)); // Increasing delay for each attempt
            };

            // Start attempts
            scrollAttempt();
        };
        
        // Function to check for scroll requests
        const checkForScrollRequests = () => {
            // Check for localStorage flag
            const scrollRequested = localStorage.getItem('scrollToTicketsRequested') === 'true';
            
            // Check for URL parameters
            const urlParams = new URLSearchParams(window.location.search);
            const scrollToTickets = urlParams.get('scrollToTickets') === 'true';
            
            // Check for hash
            const hasRegisterHash = window.location.hash === '#register-section';
            
            // If any of them are true and we haven't scrolled yet
            if ((scrollRequested || scrollToTickets || hasRegisterHash) && !hasScrolledOnInitialLoad) {
                // Clear the flag
                localStorage.removeItem('scrollToTicketsRequested');
                
                // Mark that we've scrolled
                hasScrolledOnInitialLoad = true;
                
                // Ensure components are visible
                setShowOtherComponents(true);
                
                // Try to scroll after a delay
                setTimeout(() => {
                    window.scrollToTickets();
                }, 1500);
            }
        };
        
        // Run this check periodically
        const checkInterval = setInterval(checkForScrollRequests, 500);
        
        // Run once on component mount
        checkForScrollRequests();
        
        // Cleanup
        return () => {
            delete window.scrollToTickets;
            clearInterval(checkInterval);
        };
    }, []);

    // Handle Scroll to show other components
    useEffect(() => {
        const handleScroll = () => {
            const windowHeight = window.innerHeight;
            const documentHeight = document.body.offsetHeight;
            const scrollPosition =
                window.scrollY ||
                window.pageYOffset ||
                document.documentElement.scrollTop;
            const maxScroll = documentHeight - windowHeight;
            const percentage = (scrollPosition / maxScroll) * 100;

            if (percentage > 5) {
                setShowOtherComponents(true);
            }
        };

        window.addEventListener("scroll", handleScroll);

        // Clean up the scroll listener when the component unmounts
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    // Adjust Image Height
    useEffect(() => {
        const adjustImageHeight = () => {
            if (absoluteContentRef.current && mainImageRef.current) {
                const absoluteContentHeight = absoluteContentRef.current.offsetHeight;
                setImageHeight(absoluteContentHeight + (isMobile ? 200 : 300));
            }
        };

        adjustImageHeight();
        window.addEventListener("resize", adjustImageHeight);

        return () => {
            window.removeEventListener("resize", adjustImageHeight);
        };
    }, [isMobile]);

    // Set Banner Sources
    useEffect(() => {
        const bodyBgType = isMobile ? "mobile" : "desktop";
        setBodyBgImgSrc(`${EVENT_TEMPLATES_CDN_URL}assests/angelai-custom-event/theme${themeId}/${bodyBgType}-body.webp`);
    }, [themeId, isMobile]);

    // Force eager loading of BuyTicket when scrolling is requested
    useEffect(() => {
        // Check if we need to scroll to tickets on first load
        if (isFirstLoad) {
            setIsFirstLoad(false);
            
            // Check URL parameters and hash
            const urlParams = new URLSearchParams(window.location.search);
            const scrollToTickets = urlParams.get('scrollToTickets') === 'true';
            const hasRegisterHash = window.location.hash === '#register-section';
            
            if (scrollToTickets || hasRegisterHash) {
                setShowOtherComponents(true);
            }
        }
    }, [isFirstLoad]);

    return (
        <div className={"bodyBackgroundContainer backgroundTheme" + themeId}>
            <div className="position-relative">
                <Suspense fallback={<SpinnerLoader />}>
                    <Suspense fallback={<div />}>
                        <SponsorSection webpageData={webpageData}/>
                    </Suspense>
                    <Suspense fallback={<div />}>
                        <Speakers webpageData={webpageData}/>
                    </Suspense>


                    <Suspense fallback={<div />}>
                        <RamaSwamySection webpageData={webpageData}/>
                    </Suspense> 


                    <Suspense fallback={<SpinnerLoader />}>
                        <CongressSection webpageData={webpageData}/>
                    </Suspense>

                    {/* <Suspense fallback={<SpinnerLoader />}>
                        <Agendasection webpageData={webpageData} />
                    </Suspense> */}

                    {/* Add the ref to this div that contains BuyTicket */}
                    {showBuyTicket && (
                        <div ref={buyTicketContainerRef} id="ticket-container-ref">
                            <Suspense fallback={<SpinnerLoader />}>
                                <BuyTicket webpageData={webpageData} carouselRef={carouselRef}/>
                            </Suspense>
                        </div>
                    )}

                    {showOtherComponents && (
                        <>
                            <Suspense fallback={<SpinnerLoader />}>
                                <Footer
                                    themeId={themeId}
                                    eventType={eventType}
                                    eventCreatedUnderDomain={eventCreatedUnderDomain}
                                    webpageData={webpageData}
                                />
                            </Suspense>
                        </>
                    )}
                </Suspense>
            </div>
        </div>
    );
}